import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { aboutSchemas } from '../lib/schemas';

/* ── Hero ── */
const HeroSection = styled.section`
  background-color: #7AA6A2;
  padding: 6rem 2rem 7rem;
  text-align: center;
  position: relative;
  z-index: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 65%;
    height: 100%;
    background-image: url('/img/background.png');
    background-repeat: no-repeat;
    background-size: cover;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 50%);
    mask-image: linear-gradient(to right, transparent 0%, black 50%);
    pointer-events: none;
    z-index: -1;
  }
`;

const HeroWave = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  line-height: 0;
  pointer-events: none;
  svg { width: 100%; height: 64px; display: block; }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

/* ── Lead (below hero) ── */
const LeadSection = styled.section`
  background-color: #fff;
  padding: 4rem 2rem 4.5rem;
  text-align: center;
`;

const LeadText = styled.p`
  color: #555;
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 600px;
  margin: 0 auto;
`;

/* ── Story ── */
const StorySection = styled.section`
  padding: 5rem 2rem;
  max-width: 780px;
  margin: 0 auto;
`;

const SectionLabel = styled.p`
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #7AA6A2;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.3;
`;

const BodyText = styled.p`
  color: #555;
  font-size: 1.05rem;
  line-height: 1.8;
  margin-bottom: 1.25rem;
`;

/* ── Team ── */
const TeamSection = styled.section`
  background-color: #FAF9F8;
  padding: 5rem 2rem;
`;

const TeamInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const TeamHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 3rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FounderCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 2.5rem;
  border: 1px solid #eee;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
`;

const FounderPhotoArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.75rem;
`;

const FounderPhoto = styled.div`
  width: 80px;
  height: 80px;
  min-width: 80px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, #7AA6A2 0%, #a8c8c5 100%);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FounderMeta = styled.div``;

const FounderName = styled.h3`
  color: #333;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
`;

const FounderTitle = styled.p`
  color: #7AA6A2;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
`;

const FounderBio = styled.p`
  color: #555;
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 1rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CredentialList = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  gap: 0.4rem 0.5rem;
  margin-top: auto;
  padding-top: 1.25rem;
`;

const Credential = styled.span`
  background-color: #f0f5f5;
  color: #5a8a87;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
`;

/* ── Vietnam context ── */
const ContextSection = styled.section`
  padding: 5rem 2rem;
  max-width: 780px;
  margin: 0 auto;
  text-align: center;
`;

const ContextText = styled.p`
  color: #555;
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.25rem;
`;

/* ── CTA ── */
const CtaSection = styled.section`
  background-color: #7AA6A2;
  padding: 4rem 2rem;
  text-align: center;
`;

const CtaTitle = styled.h2`
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const CtaText = styled.p`
  color: rgba(255,255,255,0.9);
  font-size: 1.05rem;
  max-width: 520px;
  margin: 0 auto 2rem;
  line-height: 1.7;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: #7AA6A2;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.85rem 2rem;
  border-radius: 100px;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f8f7;
    color: #5a8a87;
  }
`;

const About = () => {
  return (
    <Layout
      title="About Us"
      description="Meet Andrew Schwabe and Rebecca Joy — the entrepreneurs, researchers, and teachers behind Building on Truth."
      path="/about"
      image="/img/andrew_pic.JPG"
      imageAlt="Andrew Schwabe and Rebecca Joy — founders of Building on Truth"
      keywords="Andrew Schwabe, Rebecca Schwabe, Building on Truth founders, Christian entrepreneurs, faith and AI"
      jsonLd={aboutSchemas()}
    >
      <HeroSection>
        <HeroTitle>About Us</HeroTitle>
        <HeroWave>
          <svg viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64 C360,0 1080,0 1440,64 L1440,64 L0,64 Z" fill="#fff" />
          </svg>
        </HeroWave>
      </HeroSection>

      <LeadSection>
        <LeadText>
          Practitioners first. Teachers second. We bring decades of real-world experience to every conversation about faith, business, and&nbsp;technology.
        </LeadText>
      </LeadSection>

      {/* Story */}
      <StorySection>
        <SectionLabel>Our Story</SectionLabel>
        <SectionTitle>We didn't plan to start a teaching ministry</SectionTitle>
        <BodyText>
          Andrew and Rebecca were busy running companies — building AI systems, advising startups, working with Fortune 100 clients across Southeast Asia — when they kept running into the same question from the Christians around them: <em>As Christians, should we be using AI?</em>
        </BodyText>
        <BodyText>
          How should a Christian approach AI? Is entrepreneurship a legitimate calling, or is it somehow less spiritual than "real ministry"? What does it mean to run a business in a way that actually honors God — not just with a verse on the wall, but in the culture, the decisions, the product itself?
        </BodyText>
        <BodyText>
          These weren't abstract questions for Andrew and Rebecca. They were the questions they were living. So they started teaching — at churches, at conferences, at universities in Vietnam and the US — and found that the hunger for this kind of grounded, practical, biblically-serious engagement was everywhere.
        </BodyText>
        <BodyText>
          Building on Truth is the result: a ministry dedicated to helping Christians think clearly and act faithfully at the intersection of faith, business, and technology.
        </BodyText>
      </StorySection>

      {/* Team */}
      <TeamSection>
        <TeamInner>
          <TeamHeader>
            <SectionLabel>The Team</SectionLabel>
            <SectionTitle>Meet Andrew & Rebecca</SectionTitle>
          </TeamHeader>
          <TeamGrid>
            <FounderCard>
              <FounderPhotoArea>
                <FounderPhoto><img src="/img/andrew_pic.JPG" alt="Andrew Schwabe" /></FounderPhoto>
                <FounderMeta>
                  <FounderName>Andrew Schwabe</FounderName>
                  <FounderTitle>Co-Founder · AI Researcher · Serial Entrepreneur</FounderTitle>
                </FounderMeta>
              </FounderPhotoArea>
              <FounderBio>
                Andrew spent three decades building technology companies before anyone was asking how Christians should think about it. He's worked in cloud infrastructure, analytics, blockchain, and AI — not as a spectator, but as a builder. He co-founded Saigon A.I., leads AI research at the University of St. Andrews in Scotland, and founded QCoda.
              </FounderBio>
              <FounderBio>
                What drives his teaching is a deep conviction that technology is a mirror: it reflects the hearts of the people who build and use it. That makes it a profoundly spiritual subject — one the Church can't afford to ignore. Andrew teaches with theological depth, genuine optimism about the role Christians can play in the tech world, and the credibility that comes from having actually done the work.
              </FounderBio>
              <CredentialList>
                <Credential>AI Researcher, St. Andrews</Credential>
                <Credential>Co-Founder, Saigon A.I.</Credential>
                <Credential>Founder/CEO, QCoda</Credential>
                <Credential>TEDx Speaker</Credential>
              </CredentialList>
            </FounderCard>

            <FounderCard>
              <FounderPhotoArea>
                <FounderPhoto><img src="/img/rebecca_pic.png" alt="Rebecca Joy" /></FounderPhoto>
                <FounderMeta>
                  <FounderName>Rebecca Joy</FounderName>
                  <FounderTitle>Co-Founder · Data Scientist · CEO, Saigon A.I.</FounderTitle>
                </FounderMeta>
              </FounderPhotoArea>
              <FounderBio>
                Rebecca has co-founded six companies across FinTech, InsurTech, and Logistics — her most recent being Saigon A.I., where she leads a data science team serving Global Fortune 100 clients. Educated at UC Berkeley, she brings deep expertise in statistics, analytics, and data science to her work.
              </FounderBio>
              <FounderBio>
                But what animates her teaching is something she's seen play out in real businesses: that "business as a mission" isn't a strategy — it's a posture. It's about creating genuine value, serving communities well, and letting the character of Christ show up in how you treat your customers, your team, and your competitors. Rebecca challenges believers to stop thinking of their work as secondary to "real ministry" and start seeing it as one of the most powerful platforms for gospel witness available to them.
              </FounderBio>
              <CredentialList>
                <Credential>CEO, Saigon A.I.</Credential>
                <Credential>UC Berkeley</Credential>
                <Credential>6 Startups Founded</Credential>
                <Credential>Women in Tech Advocate</Credential>
              </CredentialList>
            </FounderCard>
          </TeamGrid>
        </TeamInner>
      </TeamSection>

      {/* Vietnam context */}
      <ContextSection>
        <SectionLabel>Where We Work</SectionLabel>
        <SectionTitle>Rooted in Vietnam, teaching globally</SectionTitle>
        <ContextText>
          Most of our events have taken place in Ho Chi Minh City, Hanoi, and Da Nang — one of the world's fastest-growing tech ecosystems. We're not teaching about AI and entrepreneurship from the sidelines; we're building companies in the middle of it, in a context where business as a mission isn't theoretical.
        </ContextText>
        <ContextText>
          We also speak regularly in the US and work with organizations around the world. If you're wondering whether our teaching applies to your context — it does.
        </ContextText>
      </ContextSection>

      {/* CTA */}
      <CtaSection>
        <CtaTitle>Want to learn more?</CtaTitle>
        <CtaText>
          Book a free call, attend an event, or bring us to your organization. We'd love to connect.
        </CtaText>
        <CtaButton to="/contact">Book a Free Call</CtaButton>
      </CtaSection>
    </Layout>
  );
};

export default About;
